import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, PlusCircle, X, Buildings, UsersThree, CaretDown, CaretUp,
  Prohibit, ArrowCounterClockwise, Key, CheckCircle, PencilSimple, Trash,
} from "@phosphor-icons/react";

const PROPERTY_TYPES = ["hotel", "resort", "restaurant", "cafe", "qsr", "bar", "campus"];
const EMPTY_BRAND = { org_name: "", property_type: "hotel", founder_name: "", founder_email: "", founder_username: "", founder_password: "", founder_title: "", manager_name: "", manager_email: "", manager_username: "", manager_password: "" };
const ROLE_LABEL = { admin: "Property Head", manager: "Manager", supervisor: "Supervisor", staff: "Staff" };

function UserRow({ u, onChanged }) {
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ name: u.name, email: u.email, username: u.username || "" });
  const active = u.active !== false;

  const setAccess = async (next) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: next });
      toast.success(next ? `${u.name} restored` : `${u.name}'s access revoked`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetPw = async () => {
    if (pw.length < 6) return toast.error("New password needs 6+ characters");
    try {
      await api.put(`/admin/users/${u.id}`, { password: pw });
      toast.success(`Password reset for ${u.name}`);
      setPw(""); setShowPw(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${u.id}`, edit);
      toast.success(`${edit.name} updated`);
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteUser = async () => {
    if (!window.confirm(`Permanently delete ${u.name}? Their username "${u.username || ""}" will be released for reuse immediately.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`${data.deleted} deleted — username "${data.username_released}" is available again`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={`admin-user-row-${u.id}`} className="py-3 border-b border-slate-50 last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm font-bold">{u.name}
            {!active && <span className="ml-2 text-[10px] font-bold uppercase bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">Revoked</span>}
          </p>
          <p className="text-xs text-slate-400">{u.email} · <span className="font-semibold text-slate-500" data-testid={`admin-user-username-${u.id}`}>@{u.username || "—"}</span></p>
        </div>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-600">{u.role === "admin" ? (u.position || "Property Head") : (ROLE_LABEL[u.role] || u.role)}</span>
        <button data-testid={`admin-user-edit-${u.id}`} onClick={() => setEditing(!editing)}
          className="h-10 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 tap-press hover:border-slate-900">
          <PencilSimple size={14} /> Edit
        </button>
        {showPw ? (
          <div className="flex gap-2">
            <input data-testid={`admin-user-pw-input-${u.id}`} type="text" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="New password" className="h-10 px-3 rounded-lg border border-slate-200 text-sm w-40" />
            <button data-testid={`admin-user-pw-save-${u.id}`} onClick={resetPw}
              className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs font-bold tap-press">Save</button>
            <button onClick={() => { setShowPw(false); setPw(""); }} className="h-10 px-3 rounded-full border border-slate-200 text-xs font-bold text-slate-500 tap-press">Cancel</button>
          </div>
        ) : (
          <button data-testid={`admin-user-reset-pw-${u.id}`} onClick={() => setShowPw(true)}
            className="h-10 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 tap-press hover:border-slate-900">
            <Key size={14} /> Reset password
          </button>
        )}
        {active ? (
          <button data-testid={`admin-user-revoke-${u.id}`} onClick={() => setAccess(false)}
            className="h-10 px-4 rounded-full border border-red-200 text-xs font-bold text-red-600 flex items-center gap-1.5 tap-press hover:bg-red-50">
            <Prohibit size={14} /> Revoke access
          </button>
        ) : (
          <button data-testid={`admin-user-restore-${u.id}`} onClick={() => setAccess(true)}
            className="h-10 px-4 rounded-full border border-emerald-200 text-xs font-bold text-emerald-700 flex items-center gap-1.5 tap-press hover:bg-emerald-50">
            <ArrowCounterClockwise size={14} /> Restore access
          </button>
        )}
        <button data-testid={`admin-user-delete-${u.id}`} onClick={deleteUser}
          className="h-10 px-3 rounded-full border border-red-200 text-xs font-bold text-red-600 flex items-center gap-1.5 tap-press hover:bg-red-50">
          <Trash size={14} /> Delete
        </button>
      </div>
      {editing && (
        <div className="flex flex-col sm:flex-row gap-2 mt-3 bg-slate-50 border border-slate-100 rounded-xl p-3" data-testid={`admin-user-edit-form-${u.id}`}>
          <input data-testid={`admin-user-name-input-${u.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            placeholder="Name" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <input data-testid={`admin-user-email-input-${u.id}`} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })}
            placeholder="Email" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <input data-testid={`admin-user-username-input-${u.id}`} value={edit.username} onChange={(e) => setEdit({ ...edit, username: e.target.value })}
            placeholder="Username" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <button data-testid={`admin-user-edit-save-${u.id}`} onClick={saveEdit}
            className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs font-bold tap-press">Save</button>
        </div>
      )}
    </div>
  );
}

function PlatformKeys() {
  const [keys, setKeys] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const FIELDS = [
    ["google_places_key", "Google Places Master Key"],
    ["resend_key", "Resend Email Key"],
    ["twilio_sid", "Twilio Account SID"],
    ["twilio_token", "Twilio Auth Token"],
    ["twilio_whatsapp_from", "Twilio WhatsApp From Number"],
  ];
  const load = () => api.get("/admin/platform-settings").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const updates = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== undefined));
    if (Object.keys(updates).length === 0) return toast.error("Nothing to save — enter a key first");
    setBusy(true);
    try {
      const { data } = await api.put("/admin/platform-settings", updates);
      setKeys(data); setForm({});
      toast.success("System Master Keys updated — brand connectors use them as fallback");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="platform-keys-card">
      <div className="flex items-center gap-3 mb-2">
        <Key size={24} weight="duotone" className="text-emerald-600" />
        <h2 className="font-heading text-xl font-bold">System Master Keys</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">Platform-wide fallback credentials for all brands. Enterprise founders can override with their own keys in Brand Vault. Stored server-side, never shown in full.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([f, label]) => (
          <div key={f}>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 flex items-center gap-2">
              {label}
              {keys?.[f]?.configured
                ? <span data-testid={`key-status-${f}`} className="text-[10px] normal-case bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">configured · {keys[f].masked}</span>
                : <span data-testid={`key-status-${f}`} className="text-[10px] normal-case bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full">not configured</span>}
            </label>
            <input data-testid={`platform-key-input-${f}`} value={form[f] ?? ""}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              placeholder="Paste new value (blank clears)" className="mt-2 w-full h-11 px-4 rounded-xl border border-slate-200 text-sm" />
          </div>
        ))}
      </div>
      <button data-testid="platform-keys-save" onClick={save} disabled={busy}
        className="mt-6 h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
        {busy ? "Saving…" : "Save master keys"}
      </button>
    </div>
  );
}


export default function DeveloperAdminPage() {
  const [orgs, setOrgs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_BRAND);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [editOrg, setEditOrg] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [featOrg, setFeatOrg] = useState(null);

  const MODULES = [
    ["academy", "Academy / Training Videos"],
    ["sops", "SOPs / Video Vault"],
    ["brand_vault", "Brand Vault"],
    ["briefings", "Daily Briefings / Micro-Checks"],
    ["reviews", "Reviews & Feedback"],
    ["tasks", "Tasks & Checklists"],
    ["audits", "Audits & Inspections (Handover)"],
    ["roster", "Duty Roster"],
    ["analytics", "Analytics & Reports"],
  ];

  const saveEditOrg = async () => {
    try {
      await api.put(`/admin/orgs/${editOrg.id}`, { name: editOrg.name, logo_url: editOrg.logo_url || "", details: editOrg.details || "", property_type: editOrg.property_type || "restaurant" });
      const newUname = (editOrg.founder_username || "").trim();
      if (editOrg.founder?.id && newUname && newUname !== (editOrg.founder.username || "")) {
        await api.put(`/admin/users/${editOrg.founder.id}`, { username: newUname });
      }
      toast.success("Brand details updated");
      setEditOrg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/orgs/${deleteOrg.id}`);
      toast.success(`Brand "${deleteOrg.name}" and all its data deleted`);
      setDeleteOrg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFeature = async (o, key) => {
    const next = { ...o.features, [key]: !o.features[key] };
    try {
      await api.put(`/admin/orgs/${o.id}/features`, next);
      setOrgs((prev) => prev.map((x) => (x.id === o.id ? { ...x, features: next } : x)));
      toast.success(`${o.name}: module ${next[key] ? "enabled" : "hidden"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = () => api.get("/admin/orgs").then((r) => setOrgs(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const loadUsers = (orgId) => api.get(`/admin/orgs/${orgId}/users`).then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));

  const toggleExpand = (orgId) => {
    if (expanded === orgId) { setExpanded(null); setUsers([]); }
    else { setExpanded(orgId); setUsers([]); loadUsers(orgId); }
  };

  const toggleSubscription = async (o) => {
    const next = o.subscription?.status === "suspended" ? "active" : "suspended";
    try {
      await api.put(`/admin/orgs/${o.id}/subscription`, { status: next });
      toast.success(next === "active" ? `${o.name} subscription reactivated` : `${o.name} suspended — brand logins are blocked`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createBrand = async () => {
    if (!form.org_name.trim() || !form.founder_name.trim() || !form.founder_email.trim())
      return toast.error("Property name, primary contact name and email are required");
    if (form.founder_password && form.founder_password.length < 6)
      return toast.error("Property head password needs 6+ characters (or leave blank to auto-generate)");
    if (form.manager_email.trim() && form.manager_password && form.manager_password.length < 6)
      return toast.error("Manager password needs 6+ characters (or leave blank to auto-generate)");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orgs", form);
      toast.success(`Brand "${form.org_name}" created — share the temporary credentials`);
      setCreated(data);
      setForm(EMPTY_BRAND);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 rise-in" data-testid="developer-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Platform Control</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <ShieldCheck size={36} weight="duotone" className="text-emerald-600" /> Developer Admin
          </h1>
          <p className="text-slate-500 mt-3">Create brand accounts, manage subscriptions, and control login access across the platform.</p>
        </div>
        <button data-testid="admin-create-brand-button" onClick={() => setShowCreate(true)}
          className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800 shrink-0">
          <PlusCircle size={18} /> New brand account
        </button>
      </div>

      <div className="space-y-4" data-testid="admin-org-list">
        {orgs.map((o) => {
          const suspended = o.subscription?.status === "suspended";
          return (
            <div key={o.id} data-testid={`admin-org-card-${o.id}`}
              className={`bg-white border border-slate-200 rounded-2xl p-6 border-l-4 ${suspended ? "border-l-red-500" : "border-l-emerald-500"}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <Buildings size={24} weight="duotone" className="text-slate-700" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-heading font-bold text-lg leading-snug">{o.name}
                    {suspended && <span className="ml-2 text-[10px] font-bold uppercase bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">Suspended</span>}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    <span className="uppercase font-bold text-slate-500">{o.property_type || "restaurant"}</span> · Primary contact: {o.founder ? `${o.founder.name} (@${o.founder.username || o.founder.email})` : "—"} · {o.user_count} user{o.user_count !== 1 ? "s" : ""} · {o.venue_count} venue{o.venue_count !== 1 ? "s" : ""}
                  </p>
                </div>
                <button data-testid={`admin-org-edit-${o.id}`} onClick={() => setEditOrg({ ...o, founder_username: o.founder?.username || "" })}
                  className="h-11 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 tap-press hover:border-slate-900">Edit brand</button>
                <button data-testid={`admin-org-modules-${o.id}`} onClick={() => setFeatOrg(featOrg === o.id ? null : o.id)}
                  className="h-11 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 tap-press hover:border-slate-900">Modules</button>
                <button data-testid={`admin-org-delete-${o.id}`} onClick={() => setDeleteOrg(o)}
                  className="h-11 px-4 rounded-full border border-red-200 text-xs font-bold text-red-600 tap-press hover:bg-red-50">Delete</button>
                <button data-testid={`admin-org-subscription-${o.id}`} onClick={() => toggleSubscription(o)}
                  className={`h-11 px-5 rounded-full text-xs font-bold tap-press border ${suspended
                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    : "border-red-200 text-red-600 hover:bg-red-50"}`}>
                  {suspended ? "Reactivate subscription" : "Suspend subscription"}
                </button>
                <button data-testid={`admin-org-expand-${o.id}`} onClick={() => toggleExpand(o.id)}
                  className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 tap-press hover:border-slate-900">
                  <UsersThree size={16} /> Manage users {expanded === o.id ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
              </div>
              {featOrg === o.id && (
                <div className="mt-5 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-3" data-testid={`admin-org-features-${o.id}`}>
                  {MODULES.map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                      <p className="text-sm font-bold">{label}</p>
                      <button data-testid={`feature-toggle-${key}-${o.id}`} onClick={() => toggleFeature(o, key)}
                        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${o.features[key] ? "bg-emerald-500" : "bg-slate-300"}`}>
                        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${o.features[key] ? "left-6" : "left-1"}`} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {expanded === o.id && (
                <div className="mt-5 pt-4 border-t border-slate-100" data-testid={`admin-org-users-${o.id}`}>
                  {users.map((u) => <UserRow key={u.id} u={u} onChanged={() => loadUsers(o.id)} />)}
                  {users.length === 0 && <p className="text-sm text-slate-400 py-2">Loading users…</p>}
                </div>
              )}
            </div>
          );
        })}
        {orgs.length === 0 && <p className="text-slate-400">No brand accounts yet — create the first one above.</p>}
      </div>

      <PlatformKeys />

      {editOrg && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setEditOrg(null)}>
          <div data-testid="admin-edit-brand-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Edit brand</h2>
              <button onClick={() => setEditOrg(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="edit-brand-name" value={editOrg.name} onChange={(e) => setEditOrg({ ...editOrg, name: e.target.value })}
                placeholder="Brand name" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1.5">Property category</p>
                <select data-testid="edit-brand-property-type" value={editOrg.property_type || "restaurant"} onChange={(e) => setEditOrg({ ...editOrg, property_type: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold capitalize">
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              {editOrg.founder?.id && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1.5">Property Head / GM login username</p>
                  <input data-testid="edit-brand-founder-username" value={editOrg.founder_username} onChange={(e) => setEditOrg({ ...editOrg, founder_username: e.target.value })}
                    placeholder="Primary contact username" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
                </div>
              )}
              <input data-testid="edit-brand-logo" value={editOrg.logo_url || ""} onChange={(e) => setEditOrg({ ...editOrg, logo_url: e.target.value })}
                placeholder="Logo URL" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <textarea data-testid="edit-brand-details" value={editOrg.details || ""} onChange={(e) => setEditOrg({ ...editOrg, details: e.target.value })}
                rows={3} placeholder="Brand details / notes" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
              <button data-testid="edit-brand-save" onClick={saveEditOrg}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">Save brand</button>
            </div>
          </div>
        </div>
      )}

      {deleteOrg && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setDeleteOrg(null)}>
          <div data-testid="admin-delete-brand-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <h2 className="font-heading text-2xl font-bold text-red-600">Delete {deleteOrg.name}?</h2>
            <p className="text-sm text-slate-600 mt-3">This permanently removes the brand, all its users, venues, SOPs, videos, tasks, reviews and history. This cannot be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="delete-brand-cancel" onClick={() => setDeleteOrg(null)}
                className="flex-1 h-13 py-3 rounded-full border-2 border-slate-200 font-bold tap-press hover:border-slate-900">Cancel</button>
              <button data-testid="delete-brand-confirm" onClick={confirmDelete}
                className="flex-1 h-13 py-3 rounded-full bg-red-600 text-white font-heading font-bold tap-press hover:bg-red-700">Delete permanently</button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => { setShowCreate(false); setCreated(null); }}>
          <div data-testid="admin-create-brand-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">{created ? "Brand created" : "New brand account"}</h2>
              <button onClick={() => { setShowCreate(false); setCreated(null); }} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            {created ? (
              <div className="space-y-4" data-testid="brand-created-credentials">
                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
                  <CheckCircle size={24} weight="duotone" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-emerald-800">{created.org_name} is live</p>
                    <p className="text-sm text-emerald-700 mt-1">Share these temporary credentials securely. Each account must set a private password at first login.</p>
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="founder-temp-credentials">
                  <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Property Head / GM</p>
                  <p className="text-sm font-bold mt-1">{created.founder.email}</p>
                  <p className="text-sm font-mono bg-white border border-slate-200 rounded-lg px-3 py-2 mt-2 select-all">{created.founder.temp_password}</p>
                </div>
                {created.manager && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="manager-temp-credentials">
                    <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Manager</p>
                    <p className="text-sm font-bold mt-1">{created.manager.email}</p>
                    <p className="text-sm font-mono bg-white border border-slate-200 rounded-lg px-3 py-2 mt-2 select-all">{created.manager.temp_password}</p>
                  </div>
                )}
                <button data-testid="brand-created-done" onClick={() => { setShowCreate(false); setCreated(null); }}
                  className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">Done — credentials shared</button>
              </div>
            ) : (
            <div className="space-y-4">
              <input data-testid="brand-name-input" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                placeholder="Property / brand name" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <select data-testid="brand-property-type-select" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold capitalize">
                {PROPERTY_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t.toUpperCase()}</option>)}
              </select>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 pt-2">Property Head / GM account</p>
              <input data-testid="brand-founder-name-input" value={form.founder_name} onChange={(e) => setForm({ ...form, founder_name: e.target.value })}
                placeholder="Primary Contact Name (Property Head / GM)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <input data-testid="brand-founder-title-input" value={form.founder_title} onChange={(e) => setForm({ ...form, founder_title: e.target.value })}
                placeholder="Account owner title (e.g. General Manager, Managing Director)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <input data-testid="brand-founder-username-input" value={form.founder_username} onChange={(e) => setForm({ ...form, founder_username: e.target.value })}
                placeholder="Username (login credential, blank = from email)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="brand-founder-email-input" type="email" value={form.founder_email} onChange={(e) => setForm({ ...form, founder_email: e.target.value })}
                  placeholder="Primary contact email" className="h-12 px-4 rounded-xl border border-slate-200" />
                <input data-testid="brand-founder-password-input" type="text" value={form.founder_password} onChange={(e) => setForm({ ...form, founder_password: e.target.value })}
                  placeholder="Blank = auto temp password" className="h-12 px-4 rounded-xl border border-slate-200" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 pt-2">Manager account (optional)</p>
              <input data-testid="brand-manager-name-input" value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })}
                placeholder="Manager full name" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <input data-testid="brand-manager-username-input" value={form.manager_username} onChange={(e) => setForm({ ...form, manager_username: e.target.value })}
                placeholder="Username (login credential, blank = from email)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="brand-manager-email-input" type="email" value={form.manager_email} onChange={(e) => setForm({ ...form, manager_email: e.target.value })}
                  placeholder="Manager email" className="h-12 px-4 rounded-xl border border-slate-200" />
                <input data-testid="brand-manager-password-input" type="text" value={form.manager_password} onChange={(e) => setForm({ ...form, manager_password: e.target.value })}
                  placeholder="Blank = auto temp password" className="h-12 px-4 rounded-xl border border-slate-200" />
              </div>
              <button data-testid="brand-create-submit" onClick={createBrand} disabled={busy}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
                {busy ? "Creating…" : "Create brand account"}
              </button>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
